import {
  File,
  FileArchive,
  FileAudio,
  FileCode2,
  FileJson,
  FileSpreadsheet,
  FileText,
  FileUp,
  FileVideo,
  Image as ImageIcon,
  LoaderCircle,
  Plus,
  RefreshCw,
  X,
  XCircle,
} from "lucide-react";
import {
  type ChangeEvent,
  type DragEvent,
  type HTMLAttributes,
  useCallback,
  useRef,
  useState,
} from "react";

export type ComposerAttachmentStatus = "queued" | "uploading" | "uploaded" | "deleting" | "failed";

export interface ComposerAttachment {
  id: string;
  file: File;
  status: ComposerAttachmentStatus;
  progress: number;
  path?: string;
  error?: string;
}

let attachmentSequence = 0;

export function createComposerAttachments(files: File[]): ComposerAttachment[] {
  return files.map((file) => {
    attachmentSequence += 1;
    return {
      id: `attachment-${Date.now()}-${attachmentSequence}`,
      file,
      status: "queued",
      progress: 0,
    };
  });
}

export function ComposerUploadButton({
  disabled,
  disabledReason,
  onFiles,
}: {
  disabled: boolean;
  disabledReason?: string;
  onFiles: (files: File[]) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const title = disabled && disabledReason ? disabledReason : "添加文件";

  function selectFiles(event: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.currentTarget.files ?? []);
    event.currentTarget.value = "";
    if (files.length > 0) onFiles(files);
  }

  return (
    <span className="composer-upload-control">
      <button
        aria-label="添加文件"
        className="composer-upload-button"
        disabled={disabled}
        title={title}
        type="button"
        onClick={() => inputRef.current?.click()}
      >
        <Plus aria-hidden="true" size={17} />
      </button>
      <input
        ref={inputRef}
        aria-hidden="true"
        className="composer-file-input"
        disabled={disabled}
        multiple
        tabIndex={-1}
        type="file"
        onChange={selectFiles}
      />
    </span>
  );
}

export function ComposerAttachmentList({
  items,
  onRemove,
  onRetry,
  removeDisabled = false,
  removeDisabledReason,
}: {
  items: ComposerAttachment[];
  onRemove?: (item: ComposerAttachment) => void;
  onRetry?: (item: ComposerAttachment) => void;
  removeDisabled?: boolean;
  removeDisabledReason?: string;
}) {
  if (items.length === 0) return null;
  return (
    <div aria-label="待分析文件" aria-live="polite" className="composer-attachments">
      {items.map((item) => (
        <div
          className={`composer-attachment ${item.status}`}
          key={item.id}
          title={item.path ?? item.error ?? item.file.name}
        >
          <span className="composer-attachment-icon">
            {item.status === "queued" || item.status === "uploading" ? (
              <AttachmentProgress item={item} />
            ) : item.status === "uploaded" ? (
              <AttachmentTypeIcon file={item.file} />
            ) : item.status === "deleting" ? (
              <LoaderCircle aria-label="正在删除" className="spin" size={24} />
            ) : item.status === "failed" ? (
              <XCircle aria-label="上传失败" size={24} />
            ) : (
              <FileUp aria-hidden="true" size={24} />
            )}
          </span>
          <span className="composer-attachment-copy">
            <strong>{item.file.name}</strong>
            <small>
              {formatAttachmentSize(item.file.size)} · {attachmentStatusLabel(item)}
            </small>
          </span>
          {item.status === "failed" && onRetry && (
            <button
              aria-label={`重试上传 ${item.file.name}`}
              className="composer-attachment-retry"
              title="重试上传"
              type="button"
              onClick={() => onRetry(item)}
            >
              <RefreshCw size={13} />
            </button>
          )}
          {onRemove && (
            <button
              aria-label={`删除 ${item.file.name}`}
              className="composer-attachment-delete"
              disabled={item.status === "deleting" || removeDisabled}
              title={removeDisabled ? removeDisabledReason : "删除文件"}
              type="button"
              onClick={() => onRemove(item)}
            >
              <X size={13} />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

export function FileDropOverlay({ active }: { active: boolean }) {
  if (!active) return null;
  return (
    <div aria-hidden="true" className="file-drop-overlay">
      <FileUp size={20} />
      <span>松开以上传到 /project/input</span>
    </div>
  );
}

export function useComposerDropZone({
  disabled,
  disabledReason,
  onBlocked,
  onFiles,
}: {
  disabled: boolean;
  disabledReason?: string;
  onBlocked: (message: string) => void;
  onFiles: (files: File[]) => void;
}): {
  dragging: boolean;
  dropZoneProps: HTMLAttributes<HTMLElement>;
} {
  const [dragging, setDragging] = useState(false);
  const dragDepth = useRef(0);

  const handleDragEnter = useCallback(
    (event: DragEvent<HTMLElement>) => {
      if (!hasFiles(event)) return;
      event.preventDefault();
      dragDepth.current += 1;
      if (!disabled) setDragging(true);
    },
    [disabled],
  );

  const handleDragOver = useCallback(
    (event: DragEvent<HTMLElement>) => {
      if (!hasFiles(event)) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = disabled ? "none" : "copy";
    },
    [disabled],
  );

  const handleDragLeave = useCallback((event: DragEvent<HTMLElement>) => {
    if (!hasFiles(event)) return;
    event.preventDefault();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragging(false);
  }, []);

  const handleDrop = useCallback(
    (event: DragEvent<HTMLElement>) => {
      if (!hasFiles(event)) return;
      event.preventDefault();
      dragDepth.current = 0;
      setDragging(false);
      if (disabled) {
        onBlocked(disabledReason || "当前不能上传文件。");
        return;
      }
      if (containsDirectory(event)) {
        onBlocked("暂不支持上传文件夹，请选择其中的普通文件。");
        return;
      }
      const files = Array.from(event.dataTransfer.files);
      if (files.length > 0) onFiles(files);
    },
    [disabled, disabledReason, onBlocked, onFiles],
  );

  return {
    dragging,
    dropZoneProps: {
      onDragEnter: handleDragEnter,
      onDragLeave: handleDragLeave,
      onDragOver: handleDragOver,
      onDrop: handleDrop,
    },
  };
}

function hasFiles(event: DragEvent<HTMLElement>) {
  return Array.from(event.dataTransfer.types).includes("Files");
}

function containsDirectory(event: DragEvent<HTMLElement>) {
  return Array.from(event.dataTransfer.items).some((item) => {
    const entry = (
      item as DataTransferItem & {
        webkitGetAsEntry?: () => { isDirectory?: boolean } | null;
      }
    ).webkitGetAsEntry?.();
    return entry?.isDirectory === true;
  });
}

function attachmentStatusLabel(item: ComposerAttachment) {
  if (item.status === "uploading") return `上传中 ${Math.round(item.progress)}%`;
  if (item.status === "uploaded") return "已上传";
  if (item.status === "deleting") return "正在删除";
  if (item.status === "failed") return item.error || "上传失败";
  return "待上传";
}

function AttachmentProgress({ item }: { item: ComposerAttachment }) {
  const progress = Math.max(0, Math.min(100, item.progress));
  const circumference = 2 * Math.PI * 13;
  return (
    <span
      aria-label={item.status === "queued" ? "等待上传" : `已上传 ${Math.round(progress)}%`}
      aria-valuemax={100}
      aria-valuemin={0}
      aria-valuenow={Math.round(progress)}
      className={`attachment-progress ${item.status}`}
      role="progressbar"
    >
      <svg aria-hidden="true" viewBox="0 0 32 32">
        <circle className="attachment-progress-track" cx="16" cy="16" r="13" />
        <circle
          className="attachment-progress-value"
          cx="16"
          cy="16"
          r="13"
          style={{
            strokeDasharray: circumference,
            strokeDashoffset: circumference * (1 - progress / 100),
          }}
        />
      </svg>
    </span>
  );
}

function AttachmentTypeIcon({ file }: { file: File }) {
  const extension = file.name.split(".").pop()?.toLocaleLowerCase() ?? "";
  const common = { "aria-hidden": true, size: 25 } as const;
  if (file.type.startsWith("image/")) return <ImageIcon {...common} />;
  if (file.type.startsWith("audio/")) return <FileAudio {...common} />;
  if (file.type.startsWith("video/")) return <FileVideo {...common} />;
  if (["csv", "tsv", "xls", "xlsx", "ods"].includes(extension)) {
    return <FileSpreadsheet {...common} />;
  }
  if (extension === "json" || extension === "jsonl") return <FileJson {...common} />;
  if (["zip", "gz", "tgz", "bz2", "xz", "7z", "tar"].includes(extension)) {
    return <FileArchive {...common} />;
  }
  if (["py", "r", "js", "ts", "tsx", "jsx", "sh", "sql"].includes(extension)) {
    return <FileCode2 {...common} />;
  }
  if (
    file.type.startsWith("text/") ||
    ["txt", "md", "fa", "fasta", "fq", "fastq", "pdb", "cif", "yaml", "yml"].includes(extension)
  ) {
    return <FileText {...common} />;
  }
  return <File {...common} />;
}

function formatAttachmentSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) {
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  }
  return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GB`;
}
